import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { useUserNavigation } from '@forwardflow/hooks/customHooks/useUserNavigation'
import { UserService } from '@forwardflow/services/UserService'
import { useAppStore } from '@forwardflow/store'
import { PageLoader } from '@forwardflow/ui'
import { Button } from '@forwardflow/ui-v2'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { message } from 'antd'
import { useState, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

export const ProtectedRoutesLayout = () => {
   const navigate = useNavigate()
   const location = useLocation()
   const { setCurrentOrganization } = useAppStore()
   const { navigateUser, getCurrentOrgId } = useUserNavigation()
   const {
      data: user,
      isLoading: isUserLoading,
      mutate,
   } = useAuthUser({
      revalidateOnMount: true,
   })

   // Track when invitation mutations are in progress to prevent race conditions
   const [isMutatingInvitation, setIsMutatingInvitation] = useState(false)

   useEffect(() => {
      const navigateTo = async () => {
         // Prevent navigation during invitation mutations to avoid race conditions
         if (isMutatingInvitation || isUserLoading) {
            return
         }

         // Allow operators to navigate freely after initial page load
         if (isOperatorEmail(user?.email || '')) {
            return
         }

         await navigateUser({
            currentOrgId: (await getCurrentOrgId()) || '',
            searchParams: location.search,
            shouldNotNavigateToDashboard: true,
         })
      }
      navigateTo()
   }, [
      user,
      isMutatingInvitation,
      location.pathname,
      isUserLoading,
      navigateUser,
      location.search,
      getCurrentOrgId,
   ])

   if (isUserLoading || isMutatingInvitation) {
      return <PageLoader />
   }

   const acceptOrganizationInvite = async (id: string) => {
      setIsMutatingInvitation(true)
      try {
         await UserService.acceptOrganizationInvitation(id)
         message.success('Invitation accepted successfully')
         setCurrentOrganization(id)
         navigate(`/dashboard${location.search}`)
         await mutate()
      } catch (error) {
         message.error('Error accepting invitation')
      } finally {
         setIsMutatingInvitation(false)
      }
   }

   const declineOrganizationInvite = async (id: string) => {
      setIsMutatingInvitation(true)
      try {
         await UserService.rejectOrganizationInvitation(id)
         message.success('Invitation declined successfully')
         navigate(`/dashboard${location.search}`)
         await mutate()
      } catch (error) {
         message.error('Error declining invitation')
      } finally {
         setIsMutatingInvitation(false)
      }
   }

   // Show invitations if user has any
   if (user?.invitations?.length !== 0) {
      return (
         <div className="flex items-center justify-center min-h-screen">
            <div className="flex min-h-screen px-4 lg:px-[30px] flex-col items-center text-center pt-14 md:pt-28">
               <a href="/">
                  <img
                     width={'165px'}
                     src="/images/fff-logo.png"
                     className="md:absolute left-10 top-10"
                     alt="ff icon"
                  />
               </a>
            </div>
            <div className="w-full max-w-[680px] p-8 rounded-xl shadow-lg bg-[#222327]/80">
               <h1 className="mb-4 text-lg text-gray-300">
                  Organization Invitations Pending
               </h1>
               <h2 className="mb-8 text-3xl font-semibold text-white">
                  Accept an Organization to Join
               </h2>

               <div className="space-y-4">
                  {user?.invitations?.map((invite: any) => (
                     <div
                        key={invite.organizationId}
                        className="p-6 transition-all border border-gray-700 rounded-xl bg-gray-800/50 hover:border-primary/50"
                     >
                        <div className="flex items-center justify-between gap-x-8">
                           <div className="flex items-center gap-4">
                              <div>
                                 <p className="text-lg font-medium text-white">
                                    {invite?.organization.name}
                                 </p>
                                 <p className="text-sm text-gray-400">
                                    Click accept to join this organization
                                 </p>
                              </div>
                           </div>
                           <div className="flex items-center gap-3">
                              <Button
                                 onClick={() =>
                                    acceptOrganizationInvite(
                                       invite?.organizationId
                                    )
                                 }
                                 className="px-6 py-2 font-medium bg-primary hover:bg-primary/90 min-w-48"
                              >
                                 Accept Invitation
                              </Button>
                              <Button
                                 onClick={() =>
                                    declineOrganizationInvite(
                                       invite?.organizationId
                                    )
                                 }
                                 className="px-6 py-2 text-red-500 bg-transparent border border-red-500/50 hover:bg-red-500/10"
                              >
                                 Decline
                              </Button>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </div>
      )
   }

   return (
      <div>
         {/* <PageLoader /> */}
         <Outlet />
      </div>
   )
}
